import { BOOT_STATUS, type BootStatus, type Role } from '@shared/session';

/**
 * 라우트 메타. 라우트의 `handle` 에 `routeMeta({...})` 로 선언한다.
 * 선언이 없는 라우트는 **인증 필요**로 본다(기본 보호).
 * 경로 문자열(`pathname.startsWith`)로 권한을 가르지 않는다.
 */
export const ROUTE_AUTH = { PUBLIC: 'public', REQUIRED: 'required' } as const;
export type RouteAuth = (typeof ROUTE_AUTH)[keyof typeof ROUTE_AUTH];

export interface RouteMeta {
  auth: RouteAuth;
  /** 이 역할만 들어올 수 있다. 없으면 로그인한 누구나 */
  roles?: readonly Role[];
}

export interface RouteHandle {
  meta: RouteMeta;
}

export const DEFAULT_ROUTE_META: RouteMeta = { auth: ROUTE_AUTH.REQUIRED };

export function routeMeta(meta: RouteMeta): RouteHandle {
  return { meta };
}

function isRouteHandle(handle: unknown): handle is RouteHandle {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    'meta' in handle &&
    typeof (handle as RouteHandle).meta?.auth === 'string'
  );
}

/** 가장 안쪽 라우트의 선언이 이긴다. 안쪽에 선언이 없으면 바깥 것을 물려받는다. */
export function resolveRouteMeta(matches: readonly { handle?: unknown }[]): RouteMeta {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle;
    if (isRouteHandle(handle)) return handle.meta;
  }
  return DEFAULT_ROUTE_META;
}

export const ACCESS = {
  ALLOW: 'allow',
  LOGIN: 'login',
  FORBIDDEN: 'forbidden',
} as const;
export type Access = (typeof ACCESS)[keyof typeof ACCESS];

export function decideAccess(
  meta: RouteMeta,
  session: { status: BootStatus; role: Role | null },
): Access {
  if (meta.auth === ROUTE_AUTH.PUBLIC) return ACCESS.ALLOW;
  if (session.status !== BOOT_STATUS.AUTHED || session.role === null) return ACCESS.LOGIN;
  if (meta.roles && !meta.roles.includes(session.role)) return ACCESS.FORBIDDEN;
  return ACCESS.ALLOW;
}
